/**
 * Where the demo's CTAs send the visitor (SKE-4166, phase 6).
 *
 * The demo is an ad landing page whose whole job is to hand a warm visitor to
 * signup or to sales. Both handoffs are ordinary links — no SDK, no redirect
 * service — so the only thing that can go wrong is losing the campaign that
 * paid for the visit. That is what this module exists to prevent.
 *
 * ATTRIBUTION SURVIVES THE HANDOFF
 * --------------------------------
 * `signup-onboarding` reads UTM parameters and every ad click ID straight off
 * its own landing URL (`src/utils/analytics/utm.ts`, `ALL_KEYS`) and posts them
 * to `/signup/v2/createUser`, where the server allow-lists the same names onto
 * the user. So carrying the demo's captured attribution as query parameters on
 * the trial link is all it takes for a trial started from this demo to be
 * attributed to the ad that produced it — and NOT doing it would make every one
 * of those trials look like "direct".
 *
 * THE PATH IS `/onboarding`, NOT `/signup`
 * ----------------------------------------
 * There is no `/signup` route on the app server (only the `/signup/v2/*` API
 * endpoints); the signup SPA is served from `/onboarding`. Linking to `/signup`
 * would 404 the CTA. The server builds the same URL for the recap email — see
 * `services/demoSummaryPayload.ts` — so the two must not drift.
 */

import type { DemoAttribution } from './demoAttribution'
import { getDemoAttribution, getDemoSessionId } from './demoAttribution'

/**
 * Fallback trial destination for a build that doesn't override it. Production
 * is the only place the demo runs in anger, and this is production's signup.
 */
export const DEFAULT_SIGNUP_URL = 'https://app.skedsocial.com/onboarding'

/**
 * When the visitor arrived with no campaign of their own, the demo IS the
 * source. Applied only when `utm_source` is absent — overwriting a real
 * campaign would move a Google Ads trial into "the demo" and lose the ad spend
 * it came from. Mirrors the server's own fallback (demoSummaryPayload.ts).
 */
export const DEMO_FALLBACK_UTM_SOURCE = 'approvals-demo'
export const DEMO_FALLBACK_UTM_MEDIUM = 'product-demo'

/**
 * Query parameters `signup-onboarding` actually reads. Anything outside this
 * list is dropped by its allow-list and by the server's, so adding more would
 * only make the link longer.
 */
const TRIAL_URL_PARAMS: [keyof DemoAttribution, string][] = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmContent', 'utm_content'],
  ['utmTerm', 'utm_term'],
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
  ['gadSource', 'gad_source'],
  ['fbclid', 'fbclid'],
  ['msclkid', 'msclkid'],
  ['ttclid', 'ttclid'],
  ['twclid', 'twclid'],
  ['liFatId', 'li_fat_id'],
]

export interface BuildDemoTrialUrlInput {
  /** Signup landing URL. Defaults to the configured/production one. */
  baseUrl?: string
  attribution?: DemoAttribution
  sessionId?: string
  vertical?: string
}

/**
 * The "Start free trial" href, carrying this visit's attribution.
 *
 * Pure — every browser-derived input can be overridden — so the link can be
 * asserted in a test without a DOM.
 */
export const buildDemoTrialUrl = (
  input: BuildDemoTrialUrlInput = {}
): string => {
  const attribution = input.attribution ?? getDemoAttribution()
  const base = input.baseUrl?.trim() || getDemoSignupBaseUrl()

  let url: URL
  try {
    url = new URL(base)
  } catch {
    // A misconfigured build must not render a dead CTA.
    return DEFAULT_SIGNUP_URL
  }

  for (const [key, param] of TRIAL_URL_PARAMS) {
    const value = attribution[key]
    if (value) {
      url.searchParams.set(param, value)
    }
  }

  if (!url.searchParams.has('utm_source')) {
    url.searchParams.set('utm_source', DEMO_FALLBACK_UTM_SOURCE)
    url.searchParams.set('utm_medium', DEMO_FALLBACK_UTM_MEDIUM)
  }

  // Not in signup's allow-list, so neither is persisted on the user today — but
  // both land in the URL, which means GA4's `page_location` and the GTM
  // container see them, and they are what joins a trial back to a demo session
  // when someone asks. Widening `ALL_KEYS` to persist them is a follow-up.
  const vertical = input.vertical ?? attribution.vertical
  if (vertical) {
    url.searchParams.set('sk_demo_vertical', vertical)
  }
  const sessionId = input.sessionId ?? getDemoSessionId()
  if (sessionId) {
    url.searchParams.set('sk_demo_session', sessionId)
  }

  return url.toString()
}

/** Signup landing URL for this build. */
export const getDemoSignupBaseUrl = (): string =>
  import.meta.env.VITE_DEMO_SIGNUP_URL?.trim() || DEFAULT_SIGNUP_URL

/**
 * The sales booking URL, or null when this build has none.
 *
 * Ships dark: with no URL configured the "Book a demo call" CTA is not
 * rendered at all, rather than rendered pointing nowhere. Only https is
 * accepted — this href is put in front of a stranger, so a mistyped value must
 * fail closed rather than become an open redirect out of a Sked page.
 */
export const getDemoBookingUrl = (): string | null => {
  const value = import.meta.env.VITE_DEMO_BOOKING_URL?.trim()

  if (!value || !/^https:\/\//i.test(value)) {
    return null
  }

  return value
}
